import { type Olympus } from '@owner/olympus-client'
import { useQuery } from '@tanstack/vue-query'
import {
  computed,
  ref,
  unref,
  watch,
  type ComputedRef,
  type MaybeRef,
} from 'vue'

import { OlympusClient } from '@/OlympusClient'

const QUERY_KEY_PREFIX = ['capabilities', 'brand'] as const

const YEXT_INTEGRATION_NAMESPACE = 'online-presence'
const YEXT_INTEGRATION_CAPABILITY = 'yext-integration'
const CAPABILITY_NAMESPACES = [
  'marketing',
  'online-presence',
  'olo',
  'onboarding',
  'pos',
  'website-builder',
] as const
const CAPABILITY_NAMESPACE_FILTER = [
  ...CAPABILITY_NAMESPACES,
] as Olympus.Capabilities.NamespaceFilter

type BrandCapabilities = NonNullable<Olympus.Capabilities.BrandCapabilities>
type BrandNamespace = keyof BrandCapabilities
type CapabilityName<N extends BrandNamespace> = keyof NonNullable<
  BrandCapabilities[N]
>
type CapabilityStatus = 'active' | 'disabled'
type CapabilityPayload = Record<
  string,
  Record<string, { status?: CapabilityStatus }>
>

export const onboardingEligibleCapabilityActive = ref(false)
export const websiteBuilderCapabilityActive = ref(false)

/**
 * The latest brand-capabilities payload, mirrored into a module ref so non-component
 * code (e.g. the static nav map's `show`/`gated` resolvers) can read capability
 * status reactively via {@link getBrandCapabilityStatus}.
 */
const brandCapabilities = ref<BrandCapabilities>()

/**
 *
 * @param brandId
 */
export function useBrandCapabilitiesQuery(
  brandId: MaybeRef<string | undefined>
) {
  const query = useQuery({
    enabled: computed(() => !!unref(brandId)),
    queryFn: async ({ queryKey }) => {
      const [, , qBrandId] = queryKey

      if (!qBrandId) {
        throw new Error('Brand ID is required')
      }

      return await OlympusClient.capabilities.v1.brands.brand.getBrandCapabilities(
        {
          params: { brandId: qBrandId },
          query: { namespaces: CAPABILITY_NAMESPACE_FILTER },
        }
      )
    },
    queryKey: [...QUERY_KEY_PREFIX, brandId] as const,
    staleTime: 5 * 60 * 1000,
  })

  watch(
    query.data,
    (data) => {
      brandCapabilities.value = data
    },
    { immediate: true }
  )

  return query
}

/**
 * Reads a brand capability's status from the shared capabilities payload. Returns
 * `undefined` until the payload has loaded — callers must distinguish "not loaded"
 * from "disabled". Safe to call from module scope (the nav map) since it reads a
 * module ref rather than component-injected state.
 *
 * @param namespace
 * @param name
 */
export function getBrandCapabilityStatus<N extends BrandNamespace>(
  namespace: N,
  name: CapabilityName<N>
): CapabilityStatus | undefined {
  return getCapabilityStatus(namespace as string, name as string)
}

/**
 *
 * @param namespace
 * @param name
 */
function getCapabilityStatus(
  namespace: string,
  name: string
): CapabilityStatus | undefined {
  const namespaceGroup = (
    brandCapabilities.value as CapabilityPayload | undefined
  )?.[namespace]
  return namespaceGroup?.[name]?.status
}

/**
 *
 */
export function isYextIntegrationCapabilityActive(): boolean {
  return (
    getCapabilityStatus(
      YEXT_INTEGRATION_NAMESPACE,
      YEXT_INTEGRATION_CAPABILITY
    ) === 'active'
  )
}

/**
 *
 */
export function useYextIntegrationCapability(): ComputedRef<boolean> {
  return computed(isYextIntegrationCapabilityActive)
}

/**
 *
 */
export function useYextIntegrationCapabilityStatus(): ComputedRef<
  CapabilityStatus | undefined
> {
  return computed(() =>
    getCapabilityStatus(YEXT_INTEGRATION_NAMESPACE, YEXT_INTEGRATION_CAPABILITY)
  )
}

const inFlightCapabilitiesByBrand = new Map<
  string,
  Promise<BrandCapabilities>
>()

/**
 * Fetches the brand-capabilities payload for `brandId` into the shared module ref
 * so {@link getBrandCapabilityStatus} returns a real status instead of `undefined`.
 *
 * Router guards run on the initial navigation — before the layout components that
 * call {@link useBrandCapabilitiesQuery} have mounted — so on a direct URL or hard
 * refresh the ref is still empty and a capability gate reads `undefined` (silently
 * passing). Awaiting this in the guard closes that bypass. Concurrent calls for the
 * same brand share one request; it rejects if the fetch fails so the caller can
 * decide whether to fail open.
 *
 * @param brandId
 */
export async function ensureBrandCapabilitiesLoaded(
  brandId: string
): Promise<void> {
  let request = inFlightCapabilitiesByBrand.get(brandId)
  if (!request) {
    request = OlympusClient.capabilities.v1.brands.brand.getBrandCapabilities({
      params: { brandId },
      query: { namespaces: CAPABILITY_NAMESPACE_FILTER },
    })
    inFlightCapabilitiesByBrand.set(brandId, request)
  }

  try {
    brandCapabilities.value = await request
  } finally {
    inFlightCapabilitiesByBrand.delete(brandId)
  }
}

/**
 * Reactive "is this brand capability active?" check, modelled on Hestia's
 * `useCapability`. Use this in components instead of bespoke per-capability hooks.
 *
 * @param namespace
 * @param name
 */
export function useCapability<N extends BrandNamespace>(
  namespace: N,
  name: CapabilityName<N>
): ComputedRef<boolean> {
  return computed(() => getBrandCapabilityStatus(namespace, name) === 'active')
}

/**
 *
 * @param brandId
 */
export function useOnboardingEligibleCapability(
  brandId: MaybeRef<string | undefined>
): {
  isActive: ComputedRef<boolean>
  isLoading: ComputedRef<boolean>
} {
  const { data, isLoading } = useBrandCapabilitiesQuery(brandId)

  const isActive = computed(() => {
    const capabilities = data.value as
      | Record<string, Record<string, { status: string }>>
      | undefined
    return (
      capabilities?.onboarding?.['onboarding-eligible']?.status === 'active'
    )
  })

  watch(
    isActive,
    (v) => {
      onboardingEligibleCapabilityActive.value = v
    },
    { immediate: true }
  )

  return {
    isActive,
    isLoading: computed(() => isLoading.value),
  }
}

/**
 *
 * @param brandId
 */
export function useWebsiteBuilderCapability(
  brandId: MaybeRef<string | undefined>
): {
  isActive: ComputedRef<boolean>
  isLoading: ComputedRef<boolean>
} {
  const { data, isLoading } = useBrandCapabilitiesQuery(brandId)

  const isActive = computed(
    () =>
      data.value?.['website-builder']?.['website-builder']?.status === 'active'
  )

  watch(
    isActive,
    (v) => {
      websiteBuilderCapabilityActive.value = v
    },
    { immediate: true }
  )

  return {
    isActive,
    isLoading: computed(() => isLoading.value),
  }
}
